import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiRequest, type AccountSnapshot, type User } from "./lib/api";

const TOKEN_KEY = "lingosound_session_v2";

interface AuthContextValue {
  loading: boolean;
  token: string | null;
  account: AccountSnapshot | null;
  user: User | null;
  login(email: string, password: string): Promise<AccountSnapshot>;
  register(name: string, email: string, password: string): Promise<void>;
  activate(code: string, deviceId: string): Promise<void>;
  refresh(): Promise<AccountSnapshot | null>;
  logout(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [account, setAccount] = useState<AccountSnapshot | null>(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setAccount(null);
      setLoading(false);
      return null;
    }
    try {
      const snapshot = await apiRequest<AccountSnapshot>("/auth/me", {}, token);
      setAccount(snapshot);
      return snapshot;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setAccount(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void refresh(); }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await apiRequest<{ user: User; token: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    localStorage.setItem(TOKEN_KEY, response.token);
    setToken(response.token);
    const snapshot = await apiRequest<AccountSnapshot>("/auth/me", {}, response.token);
    setAccount(snapshot);
    return snapshot;
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const response = await apiRequest<{ user: User; token: string }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ name, email, password }),
    });
    localStorage.setItem(TOKEN_KEY, response.token);
    setToken(response.token);
    setAccount({
      user: response.user,
      license: null,
      usage: { characters: 0, requests: 0, estimated_cost_micro_usd: 0 },
      limits: { monthlyCharacters: 100_000, dailySpeechSessions: 4, speechSessionsUsed: 0, voiceSessionMinutes: 15 },
    });
  }, []);

  const activate = useCallback(async (code: string, deviceId: string) => {
    if (!token) throw new Error("Iniciá sesión");
    await apiRequest("/activation/redeem", {
      method: "POST",
      body: JSON.stringify({ code, deviceId }),
    }, token);
    await refresh();
  }, [refresh, token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAccount(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    loading,
    token,
    account,
    user: account?.user ?? null,
    login,
    register,
    activate,
    refresh,
    logout,
  }), [loading, token, account, login, register, activate, refresh, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
